import {useCallback, useEffect, useRef, useState} from 'react';
import {useQueryClient} from '@tanstack/react-query';
import {useJobsSimulationConfiguration, type StepDefinition} from '@tinct/web-components';
import {tinctApi, useGetCampaignById} from '@tinct/core-api';
import {useCampaignAnalysisJobAlert} from './use-campaign-analysis-job-alert';

export function useCampaignAnalysisJob(campaignId: string, campaignSteps: StepDefinition[]) {
  const hasStartedRef = useRef(false);
  const hasInvalidatedRef = useRef(false);
  const queryClient = useQueryClient();
  const [sseCompleted, setSseCompleted] = useState(false);

  const {data} = useGetCampaignById({
    id: campaignId,
    fields: {
      id: null,
      lastAnalysisJob: {
        fields: {
          id: null,
          startedAt: null,
          status: null,
        },
      },
    },
  }, {
    refetchInterval: 1000,
    enabled: !!campaignId,
  });

  const isAnalysisFailed = data?.lastAnalysisJob?.status === 'failed';
  const isAnalysisCompleted = sseCompleted || data?.lastAnalysisJob?.status === 'completed';

  const {start: startJobAlert, gameModal} = useCampaignAnalysisJobAlert(campaignId);

  // The funnel-level campaign query caches under a different key (the key
  // includes `fields`), and unlike every other step nothing confirms this one
  // via addConfirmedStep — so on completion, invalidate the campaign so the
  // funnel's availability/dispatcher snapshot catches up.
  useEffect(() => {
    if (isAnalysisCompleted && !hasInvalidatedRef.current) {
      hasInvalidatedRef.current = true;
      queryClient.invalidateQueries({queryKey: ['campaign', campaignId]});
    }
  }, [isAnalysisCompleted, campaignId, queryClient]);

  useEffect(() => {
    if (data?.lastAnalysisJob?.id) {
      startJobAlert(data.lastAnalysisJob.id);
    }
  }, [data?.lastAnalysisJob?.id, startJobAlert]);

  const startAnalysis = useCallback(() => {
    if (campaignId) {
      tinctApi.campaigns.initCampaignLandingPageAnalyserAgent(
        campaignId,
        (event) => {
          if (event && event.type === 'message_stop') {
            setSseCompleted(true);
          }
        },
        (error) => {
          console.error('Error in Landing page Analyser Agent:', error.errorCode);
        }
      );
    }
  }, [campaignId]);

  // Start the analysis once (new job only)
  useEffect(() => {
    if (!hasStartedRef.current && campaignId && data?.lastAnalysisJob === null) {
      hasStartedRef.current = true;
      startAnalysis();
    }
  }, [campaignId, data?.lastAnalysisJob, startAnalysis]);

  const {subJobs, allCompleted, remainingSeconds} = useJobsSimulationConfiguration({
    steps: campaignSteps,
    isAnalysisCompleted,
    startedAt: data?.lastAnalysisJob?.startedAt,
  });

  return {subJobs, allCompleted, remainingSeconds, isAnalysisFailed, startAnalysis, gameModal};
}
